import { Controller } from "@hotwired/stimulus"

// Per-row controller for a custom invoice line item.
//
// Shows the fields relevant to the selected item type and disables the others so
// they aren't submitted:
//   - "Lease": the lease-only fields (Associated lease + discount).
//   - "Products": picking a product from the modal fills a read-only name/amount card
//     with a quantity stepper; its description and amount are derived server-side from
//     the product, so only the hidden product_id + quantity are submitted. The
//     free-form description/amount fields are hidden and disabled for these rows.
//   - Anything else: a saved/manual mode switch over the free-form description + amount
//     fields. "Saved" backs the row with a company-defined custom invoice item, whose
//     description and price are fixed, so only its id is submitted; "manual" is the
//     free-form pair. The saved list is narrowed to the items configured for the row's
//     invoice item type; the switch is offered for every type regardless, and a type with
//     no saved items answers "saved" with a note saying so and pointing at creating a
//     reusable item.
//
// Types that name the charge on their own (Late Fee, Admin Fee) carry a default
// description on their <option>, which fills the manual description field in so the
// admin doesn't retype the type name.
//
// Keeps the row heading in sync with a live summary of the entered values, and
// lets the row collapse to just that heading to save screen space; the form still
// submits every row regardless of collapsed state.
export default class extends Controller {
  static targets = [
    "amountField",
    "body",
    "customItemAmount",
    "customItemDescription",
    "customItemOccurrenceAsterisk",
    "customItemOccurrenceOriginal",
    "customItemRecurringNote",
    "customItemSection",
    "customItemSelect",
    "customItemTaxable",
    "decrement",
    "descriptionField",
    "header",
    "heading",
    "increment",
    "lateFeeRuleId",
    "leaseFields",
    "leaseSelect",
    "manualModeButton",
    "noSavedItems",
    "productCard",
    "productImage",
    "productPlaceholder",
    "productSection",
    "quantityDisplay",
    "quantityInput",
    "readonlyAmount",
    "readonlyName",
    "savedModeButton",
    "savedPanel",
    "selectedProductId",
    "toggle",
    "toggleLabel",
    "type",
  ]

  static values = { collapsed: Boolean, productMax: Number, savedMode: Boolean }

  // The segmented control's on/off styling, kept here rather than in the template so
  // the two states can't drift apart.
  static SEGMENT_ON = ["bg-white", "shadow-sm", "text-gray-900"]
  static SEGMENT_OFF = ["text-gray-600"]

  connect() {
    this.syncFields()
    this.renderSummary()
    if (this.collapsedValue) this.collapse()
  }

  // Mirror each type's fields' visibility and disabled state to the selected item
  // type. A disabled field is not submitted, so non-applicable inputs never post.
  syncFields() {
    const type = this.typeTarget.value
    const isLateFee = type === "late_fee"
    const isLease = type === "lease"
    const isProductBacked = type === "products" && this.hasProductSectionTarget

    // A products row is built by its own picker, so the mode switch never applies to one.
    // Every other type gets the same switch whether or not it has saved items — a type
    // with none answers "saved" with the note rather than by hiding the choice. Neither
    // is shown before a type is picked, since which items apply isn't known yet.
    const hasSavedItems = !isProductBacked && this.filterSavedItems(type) > 0
    const isSavedItem = !isProductBacked && this.savedModeValue

    this.setRegion(this.customItemSectionTargets, !isProductBacked && type !== "")
    this.setRegion(this.noSavedItemsTargets, isSavedItem && !hasSavedItems)
    this.setRegion(this.savedPanelTargets, isSavedItem && hasSavedItems)
    this.setRegion(this.leaseFieldsTargets, isLease)
    this.setRegion(this.productSectionTargets, isProductBacked)
    this.setRegion(this.descriptionFieldTargets, !isProductBacked && !isSavedItem)
    this.setRegion(this.amountFieldTargets, !isProductBacked && !isSavedItem)

    this.syncSegmentedControl(isSavedItem)
    if (isSavedItem && hasSavedItems) this.renderCustomItemDetails()

    // A generated late fee keeps its (hidden) lease submitting so an edit doesn't
    // sever the linkage; the server keeps that lease only alongside the fee's rule.
    this.lateFeeRuleIdTarget.disabled = !isLateFee
    this.leaseSelectTarget.disabled = !isLease && !isLateFee
  }

  // Mirror the description field to the newly selected type's default. Only a blank
  // field or the type it was just switched away from is overwritten, so anything the
  // admin typed — including a generated description like "Late Fee - invoice #123" —
  // stays as it is. Wired to the type select rather than called from syncFields
  // because it must not run on connect: an existing row's description is the admin's,
  // even when it happens to read like some type's default.
  applyDefaultDescription() {
    const field = this.field("description")
    if (!field) return

    const current = field.value.trim()
    if (current !== "" && !this.defaultDescriptions.includes(current)) return

    field.value = this.defaultDescriptionFor(this.typeTarget.value)
  }

  get defaultDescriptions() {
    return this.typeOptions.map((option) => option.dataset.defaultDescription).filter(Boolean)
  }

  defaultDescriptionFor(type) {
    const option = this.typeOptions.find((o) => o.value === type)
    return option?.dataset.defaultDescription || ""
  }

  get typeOptions() {
    return Array.from(this.typeTarget.options)
  }

  // Narrow the saved <select> to the items configured for `type`, dropping a selection
  // that no longer applies and falling back to the first one that does. Returns how many
  // items remain, which is what decides whether "saved" has a picker or just the note.
  filterSavedItems(type) {
    if (!this.hasCustomItemSelectTarget) return 0

    let applicable = 0
    let selectionApplies = false

    Array.from(this.customItemSelectTarget.options).forEach((option) => {
      const applies = option.dataset.invoiceItemType === type
      option.hidden = !applies
      option.disabled = !applies
      if (!applies) return

      applicable += 1
      if (option.selected) selectionApplies = true
    })

    if (!selectionApplies) {
      const first = Array.from(this.customItemSelectTarget.options).find((o) => !o.disabled)
      this.customItemSelectTarget.value = first ? first.value : ""
    }

    return applicable
  }

  useSavedMode() {
    this.setSavedMode(true)
  }

  useManualMode() {
    this.setSavedMode(false)
  }

  setSavedMode(savedMode) {
    this.savedModeValue = savedMode
    this.syncFields()
    this.notifyChange(this.hasCustomItemSelectTarget ? this.customItemSelectTarget : null)
    this.renderSummary()
  }

  selectCustomInvoiceItem() {
    this.renderCustomItemDetails()
    this.renderSummary()
  }

  // Mirror the read-only detail panel to the selected option's data attributes.
  renderCustomItemDetails() {
    const option = this.selectedCustomItem
    if (!option) return

    this.customItemDescriptionTarget.textContent = option.dataset.description || ""
    this.customItemAmountTarget.textContent = option.dataset.amount || ""
    this.customItemOccurrenceOriginalTarget.textContent = option.dataset.occurrence || ""
    this.customItemTaxableTarget.textContent = option.dataset.taxable || ""

    // A recurring item still bills once from here: show its own occurrence struck
    // through, asterisked to the note that explains why.
    const recurring = option.dataset.recurring === "true"
    ;[
      this.customItemOccurrenceAsteriskTarget,
      this.customItemOccurrenceOriginalTarget,
      this.customItemRecurringNoteTarget,
    ].forEach((el) => el.classList.toggle("hidden", !recurring))
  }

  syncSegmentedControl(isSavedItem) {
    if (!this.hasSavedModeButtonTarget) return

    const { SEGMENT_ON, SEGMENT_OFF } = this.constructor
    const [on, off] = isSavedItem
      ? [this.savedModeButtonTarget, this.manualModeButtonTarget]
      : [this.manualModeButtonTarget, this.savedModeButtonTarget]

    on.classList.add(...SEGMENT_ON)
    on.classList.remove(...SEGMENT_OFF)
    off.classList.add(...SEGMENT_OFF)
    off.classList.remove(...SEGMENT_ON)
  }

  get selectedCustomItem() {
    if (!this.hasCustomItemSelectTarget) return null
    return this.customItemSelectTarget.selectedOptions[0] || null
  }

  setRegion(elements, visible) {
    elements.forEach((el) => {
      el.classList.toggle("hidden", !visible)
      el.querySelectorAll("select, input").forEach((field) => {
        field.disabled = !visible
      })
    })
  }

  // Fill the read-only product card from the clicked modal card's data and reveal
  // it in place of the placeholder. The modal is closed by a companion
  // dialog#close action on the same button.
  selectProduct(e) {
    const data = e.currentTarget.dataset

    this.selectedProductIdTarget.value = data.productId
    this.selectedProductIdTarget.disabled = false
    this.readonlyNameTarget.value = data.productName
    this.readonlyAmountTarget.value = data.productPrice
    this.productMaxValue = parseInt(data.productMax, 10) || 0

    if (this.hasProductImageTarget) {
      const thumb = e.currentTarget.querySelector("[data-product-thumb]")
      if (thumb) this.productImageTarget.innerHTML = thumb.innerHTML
    }

    this.productPlaceholderTarget.classList.add("hidden")
    this.productCardTarget.classList.remove("hidden")
    this.setQuantity(1)
  }

  clearProduct() {
    this.selectedProductIdTarget.value = ""
    this.quantityInputTarget.value = ""
    this.productCardTarget.classList.add("hidden")
    this.productPlaceholderTarget.classList.remove("hidden")
    this.notifyChange()
    this.renderSummary()
  }

  get quantity() {
    return parseInt(this.quantityInputTarget.value, 10) || 0
  }

  increment() {
    this.setQuantity(this.quantity + 1)
  }

  decrement() {
    this.setQuantity(this.quantity - 1)
  }

  // Clamp to [1, productMax] (0 max means unlimited), mirror it to the display and
  // stepper button states, and notify listeners so the live preview reloads.
  setQuantity(value) {
    let next = value < 1 ? 1 : value
    if (this.productMaxValue > 0 && next > this.productMaxValue) next = this.productMaxValue

    this.quantityInputTarget.value = next
    if (this.hasQuantityDisplayTarget) this.quantityDisplayTarget.textContent = next
    if (this.hasDecrementTarget) this.decrementTarget.disabled = next <= 1
    if (this.hasIncrementTarget) {
      this.incrementTarget.disabled = this.productMaxValue > 0 && next >= this.productMaxValue
    }

    this.notifyChange()
    this.renderSummary()
  }

  // Dispatch a bubbling change so the row's own summary and the form-level
  // invoice-preview both refresh off the updated hidden inputs.
  notifyChange(element = this.hasQuantityInputTarget ? this.quantityInputTarget : null) {
    if (element) element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  collapse() {
    this.setCollapsed(true)
  }

  toggleCollapse() {
    this.setCollapsed(!this.bodyTarget.classList.contains("hidden"))
  }

  setCollapsed(collapsed) {
    this.bodyTarget.classList.toggle("hidden", collapsed)
    this.headerTarget.classList.toggle("border-b", !collapsed)
    this.headerTarget.classList.toggle("pb-2", !collapsed)
    this.toggleLabelTarget.textContent = collapsed ? "Expand" : "Collapse"

    const chevron = this.toggleTarget.querySelector("svg")
    if (chevron) chevron.classList.toggle("-rotate-90", collapsed)
  }

  renderSummary() {
    const typeSelect = this.typeTarget
    const type = typeSelect.options[typeSelect.selectedIndex]?.text?.trim()

    const parts = []
    if (type) parts.push(type)

    if (
      this.typeTarget.value === "products" &&
      this.hasSelectedProductIdTarget &&
      this.selectedProductIdTarget.value
    ) {
      const name = this.readonlyNameTarget.value
      const qty = this.quantity
      if (name) parts.push(qty > 1 ? `${name} (x${qty})` : name)
    } else if (this.savedModeValue && this.selectedCustomItem) {
      const { amount, description } = this.selectedCustomItem.dataset
      if (description) parts.push(description)
      if (amount) parts.push(amount)
    } else {
      const description = this.fieldValue("description")
      const amount = this.fieldValue("cash_amount")
      if (description) parts.push(description)
      if (amount) parts.push(`$${amount}`)
    }

    this.headingTarget.textContent = parts.join(" · ") || "New invoice item"
  }

  field(name) {
    return this.bodyTarget.querySelector(`[name="create_simple_invoice[line_items][][${name}]"]`)
  }

  fieldValue(name) {
    const field = this.field(name)
    return field ? field.value.trim() : ""
  }
}
